
import React, { useState } from 'react';
import PageHeading from '../components/common/PageHeading';
import { Button, Input, Textarea } from '@heroui/react';
import { Helmet } from 'react-helmet';
import { Notify } from 'notiflix';

function Contact() {
    const DOMAIN = 'https://oxfordcollege.lk/api/routes/';

    const [form, setForm] = useState({
        studentFirstName: '',
        studentLastName: '',
        email: '',
        phone: '',
        message: '',
        date: new Date().toLocaleDateString(),
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.studentFirstName.trim()) {
            newErrors.firstName = 'First Name is required';
        }

        if (!form.studentLastName.trim()) {
            newErrors.lastName = 'Last Name is required';
        }

        if (!form.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = 'Invalid email address';
        }

        if (!form.phone.trim()) {
            newErrors.phone = 'phone number is required';
        } else if (!/^\d{10}$/.test(form.phone)) {
            newErrors.phone = 'phone number must be 10 digits';
        }

        if (!form.message.trim()) {
            newErrors.message = 'Message is required';
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = () => {
        const formErrors = validateForm();

        if (Object.keys(formErrors).length === 0) {
            fetch(DOMAIN + 'inquiry.php', {
                method: 'POST',
                body: JSON.stringify(form),
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        Notify.success('Form submitted successfully');
                        setForm({
                            studentFirstName: '',
                            studentLastName: '',
                            email: '',
                            phone: '',
                            message: '',
                            date: new Date().toLocaleDateString(),
                        });
                        return response.json();
                    } else if (response.status === 400) {
                        Notify.failure('Form submission failed. Please check the form for errors.');
                        throw new Error('Bad Request');
                    } else if (response.status === 500) {
                        Notify.failure('Form submission failed. Please try again later.');
                        throw new Error('Server Error');
                    } else {
                        Notify.failure('Form submission failed. Please try again later.');
                        throw new Error('Unknown Error');
                    }
                })
                .catch((error) => console.error('Error:', error));
        } else {
            Notify.failure('Form submission failed. Please check the form for errors.');
        }
    };

    return (
        <div className="w-full">
            <Helmet>
                <title>Contact Us - Oxford College</title>
                <meta name="description" content="Get in touch with us for inquiries, support, or feedback. We are here to assist you." />
                <meta name="keywords" content="Contact, Support, Inquiries, Feedback, Help" />
                <meta name="author" content="Oxford College - Gampaha" />
                <meta property="og:title" content="Contact Us - Oxford College - Gampaha" />
                <meta property="og:description" content="Reach out to us for assistance or information. We're happy to help." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://oxfordcollege.lk/contact" />
                <meta property="og:image" content="https://www.facebook.com/photo/?fbid=127196623392555&set=a.127196626725888" />
            </Helmet>
            <PageHeading title={'Get in touch'} imgUrl={'./assets/images/getintouch.webp'} alt={'Get in touch'} />

            <div className="mt-10 px-10 container mx-auto">
                <div className="max-w-[800px] mx-auto">
                    <h3 className="uppercase text-5xl font-bebas text-center">We're here to help you</h3>
                    <p className="text-center text-neutral-400 mt-6">
                        At Oxford College - Gampaha, we&apos;re committed to providing quality education and supporting each student&apos;s journey. Contact us to learn more about our programs and how we can assist you with any questions about admissions, programs, or services.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 mt-6 gap-4">
                        {/* Staff cards */}
                    </div>
                </div>
            </div>

            <div className="mt-10 px-10 container mx-auto">
                <div className="grid grid-cols-1 gap-6">
                    <div className="grid grid-cols-2 gap-4 my-20">
                        {/* First Name */}
                        <div>
                            <Input
                                variant="bordered"
                                label="Enter your first name"
                                type="text"
                                onChange={handleChange}
                                name="studentFirstName"
                                value={form.studentFirstName}
                            />
                            {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                        </div>

                        {/* Last Name */}
                        <div>
                            <Input
                                variant="bordered"
                                label="Enter your last name"
                                type="text"
                                onChange={handleChange}
                                name="studentLastName"
                                value={form.studentLastName}
                            />
                            {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                        </div>

                        {/* Email */}
                        <div className="col-span-full">
                            <Input
                                variant="bordered"
                                label="Enter your email address"
                                type="email"
                                onChange={handleChange}
                                name="email"
                                value={form.email}
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        </div>

                        {/* phone */}
                        <div className="col-span-full">
                            <Input
                                variant="bordered"
                                label="Enter your phone number"
                                type="text"
                                onChange={handleChange}
                                name="phone"
                                value={form.phone}
                            />
                            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                        </div>

                        {/* Message */}
                        <div className="col-span-full">
                            <Textarea
                                variant="bordered"
                                label="Message"
                                onChange={handleChange}
                                name="message"
                                value={form.message}
                            />
                            {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                        </div>

                        {/* Submit Button */}
                        <div className="col-span-full flex items-center justify-center">
                            <Button
                                className="bg-primary-blue text-white px-20 w-fit"
                                radius="sm"
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                        </div>
                    </div>

                    <div className='w-full flex flex-col gap-4'>
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className='w-full'>
                                <h4 className='font-bebas uppercase text-2xl'>call us</h4>
                                <a href="tel:+94703132302" className='block'>+94 70 31 32 302</a>
                                <a href="tel:+94763706240" className='block'>+94 76 37 06 240</a>
                            </div>

                            <div className='w-full'>
                                <h4 className='font-bebas uppercase text-2xl'>email us</h4>
                                <a href="mailto:info@oxfordcollege.lk" className='block'>info@oxfordcollege.lk</a>
                            </div>

                            <div className='w-full'>
                                <h4 className='font-bebas uppercase text-2xl'>address</h4>
                                <p className='block'>200A 1/1, Sandares Mawatha, Ihalagama, Gampaha</p>
                            </div>
                        </div>

                        <div className='w-full rounded-lg overflow-hidden'>
                            <iframe className='w-full h-[250px]'
                                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d307.38435996887773!2d80.0052699873519!3d7.0952267832213805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2slk!4v1737711607145!5m2!1sen!2slk"
                                allowFullScreen="" loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Contact;